import React, { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { MONAD_CHAIN_ID, MONAD_CHAIN_NAME, MONAD_RPC_URL, MONAD_EXPLORER_URL, CONTRACTS, ABIS } from '../monad/config';

const Web3Context = createContext();

export const useWeb3 = () => useContext(Web3Context);

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState("0.00");
  const [nativeBalance, setNativeBalance] = useState("0.00");
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [provider, setProvider] = useState(null);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (window.ethereum) {
      const browserProvider = new ethers.BrowserProvider(window.ethereum);
      setProvider(browserProvider);

      // Check if already connected
      window.ethereum.request({ method: 'eth_accounts' }).then((accounts) => {
        if (accounts.length > 0) {
          handleAccountChange(accounts[0], browserProvider);
        }
      });

      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length > 0) handleAccountChange(accounts[0], browserProvider);
        else disconnectWallet();
      });

      // Listen for network changes
      window.ethereum.on('chainChanged', () => window.location.reload());
    }
  }, []);

  // --- HELPERS ---
  const handleAccountChange = async (newAccount, currentProvider) => {
    setAccount(newAccount);
    setIsConnected(true);
    await fetchRealBalances(newAccount, currentProvider);
  };

  const fetchRealBalances = async (userAddress, currentProvider) => {
    try {
      // 1. Fetch Native MON (Gas Token)
      const rawNative = await currentProvider.getBalance(userAddress);
      const fmtNative = ethers.formatEther(rawNative);
      setNativeBalance(parseFloat(fmtNative).toFixed(4));

      // 2. Fetch GUARD Token (Social-Fi Rewards)
      // Uses the contract address from our config/env
      if (CONTRACTS.GUARD_TOKEN && CONTRACTS.GUARD_TOKEN.startsWith("0x")) {
        const signer = await currentProvider.getSigner();
        const tokenContract = new ethers.Contract(CONTRACTS.GUARD_TOKEN, ABIS.GUARD_TOKEN, signer);
        try {
            const rawToken = await tokenContract.balanceOf(userAddress);
            const fmtToken = ethers.formatUnits(rawToken, 18);
            setBalance(parseFloat(fmtToken).toFixed(2));
        } catch(e) {
            console.warn("GUARD Token fetch failed. Contract might not be deployed on this chain.", e);
        }
      } 
    } catch (error) {
      console.error("Balance Sync Error:", error);
    }
  };

  // --- WALLET ACTIONS ---
  const connectWallet = async () => {
    if (!window.ethereum) return alert("MetaMask not detected! Please install the extension.");
    
    setIsConnecting(true);
    try {
      // Trigger MetaMask Login
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const newProvider = new ethers.BrowserProvider(window.ethereum);
      setProvider(newProvider);
      
      // Auto-switch to Monad upon connection
      await switchToMonad();
      await handleAccountChange(accounts[0], newProvider);
    } catch (error) {
      console.error("Connection Failed:", error);
    }
    setIsConnecting(false);
  };

  const disconnectWallet = () => {
    setAccount(null);
    setIsConnected(false);
    setBalance("0.00");
    setNativeBalance("0.00");
  };

  // --- NETWORK MANAGEMENT ---
  const switchToMonad = async () => {
    if (!window.ethereum) return;

    try {
      // Try to switch to the existing network in MetaMask
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: MONAD_CHAIN_ID }], // Chain ID 10143
      });
    } catch (switchError) {
      // Code 4902 means the network is not added to MetaMask yet
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: MONAD_CHAIN_ID,
              chainName: MONAD_CHAIN_NAME,
              nativeCurrency: { 
                name: 'Monad', 
                symbol: 'MON', 
                decimals: 18 
              },
              rpcUrls: [MONAD_RPC_URL],
              blockExplorerUrls: [MONAD_EXPLORER_URL],
            }],
          });
        } catch (addError) {
          console.error("User rejected adding Monad network.", addError);
        }
      }
    }
  };

  // Format address for UI (e.g., 0x1234...5678)
  const formatAddress = (addr) => {
    if (!addr || typeof addr !== 'string') return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <Web3Context.Provider value={{ 
      account, 
      balance, 
      nativeBalance,
      connectWallet, 
      disconnectWallet, 
      provider, 
      switchToMonad, 
      isConnected, 
      isConnecting,
      formatAddress 
    }}>
      {children}
    </Web3Context.Provider>
  );
};

export default Web3Provider;